import { useState } from "react";
import { CopyCode } from "@/components/CopyCode";
import { useOutletContext } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Trophy, CheckCircle2, XCircle, DollarSign, Wallet, Smartphone, Banknote, Lock, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { formatBs, formatUsd, formatCop, formatDate } from "@/lib/format";
import { StatusBadge } from "@/pages/admin/DashboardPage";
import { PosLockedOverlay } from "@/components/layout/PosShell";

// Aislamiento estricto: cada monto se muestra SOLO en la moneda nativa del ticket.
const CUR = {
  BS: { prefix: "Bs", fmt: formatBs, key: "bs", step: "0.01" },
  USD: { prefix: "$", fmt: formatUsd, key: "usd", step: "0.01" },
  COP: { prefix: "$ COP", fmt: formatCop, key: "cop", step: "1" },
};
const curOf = (t) => CUR[t?.currency] || CUR.BS;
const money = (c, n) => `${c.prefix} ${c.fmt(n)}`;

function PlayRow({ p, i, ticket, c }) {
  const win = !!p.is_winner;
  return (
    <div
      data-testid={`pos-validator-play-${i}`}
      data-winner={win ? "true" : "false"}
      className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 rounded-lg border px-3 py-2.5 transition-colors ${win ? "border-emerald-500/40 bg-emerald-500/10 dark:bg-emerald-950/30" : "border-border bg-secondary/30 opacity-75"}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        {win ? <Trophy size={18} className="text-emerald-600 dark:text-emerald-300 shrink-0"/> : <XCircle size={18} className="text-muted-foreground shrink-0"/>}
        <div className="min-w-0">
          <div className="text-xs font-semibold break-words" data-testid={`pos-validator-play-${i}-lottery`}>
            {p.lottery_name || "Lotería"}{p.sorteo_time ? ` - ${p.sorteo_time}` : ""}
          </div>
          <div className="font-mono text-lg leading-tight">
            {p.number}{p.sign ? <span className="text-violet-600 dark:text-violet-300 text-sm ml-1">· {p.sign}</span> : ""}
          </div>
          <div className="text-[11px] text-muted-foreground tabular-nums" data-testid={`pos-validator-play-${i}-meta`}>
            {money(c, p[`amount_${c.key}`])} · x{p.payout_multiplier} · Emitido {formatDate(ticket.created_at)}
          </div>
        </div>
      </div>
      {win ? (
        <span className="self-start sm:self-auto shrink-0 rounded-md bg-emerald-600 text-white px-2.5 py-1 text-xs font-bold tabular-nums" data-testid={`pos-validator-play-${i}-winner-badge`}>
          GANADOR · {money(c, p[`payout_${c.key}`])}
        </span>
      ) : (
        <span className="shrink-0 text-[11px] uppercase tracking-widest text-muted-foreground">Sin premio</span>
      )}
    </div>
  );
}

function PayDialog({ open, onOpenChange, ticket, c, onPaid }) {
  const [deduct, setDeduct] = useState("");
  const [method, setMethod] = useState("efectivo");
  const payout = ticket?.[`payout_total_${c.key}`] || 0;
  const raw = parseFloat(deduct) || 0;
  const invalid = raw < 0 || raw > payout;
  const dNum = Math.min(Math.max(c.key === "cop" ? Math.round(raw) : raw, 0), payout);
  const net = c.key === "cop" ? Math.round(payout - dNum) : Math.round((payout - dNum) * 100) / 100;

  const pay = useMutation({
    mutationFn: async () => (await api.post(`/tickets/${ticket.id}/pay`, { deduct_amount: dNum, method })).data,
    onSuccess: (t) => {
      toast.success(`Premio pagado · Neto ${money(c, t[`payout_net_${c.key}`])}`);
      setDeduct(""); setMethod("efectivo");
      onPaid(t);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border" data-testid="pos-pay-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Trophy size={18} className="text-emerald-600 dark:text-emerald-300"/> Pagar premio · {ticket?.currency}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Premio bruto</div>
            <div className="font-display text-xl font-semibold tabular-nums text-amber-700 dark:text-amber-200" data-testid="pos-pay-total">{money(c, payout)}</div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Monto a descontar ({c.prefix}) — jugadas nuevas o deuda</Label>
            <Input type="number" min="0" step={c.step} value={deduct} onChange={(e) => setDeduct(e.target.value)}
              placeholder={c.key === "cop" ? "0" : "0.00"} className="font-mono text-lg" data-testid="pos-pay-deduct-input"/>
            {invalid && (
              <div className="text-[11px] text-rose-600 dark:text-rose-300" data-testid="pos-pay-deduct-error">
                El descuento no puede superar el premio ({money(c, payout)}).
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Forma de pago del remanente</Label>
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant={method === "efectivo" ? "default" : "outline"} onClick={() => setMethod("efectivo")} data-testid="pos-pay-method-efectivo">
                <Banknote size={16} className="mr-1.5"/> Efectivo
              </Button>
              <Button type="button" variant={method === "pago_movil" ? "default" : "outline"} onClick={() => setMethod("pago_movil")} data-testid="pos-pay-method-pagomovil">
                <Smartphone size={16} className="mr-1.5"/> Pago móvil
              </Button>
            </div>
          </div>
          <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 dark:text-emerald-200"><Wallet size={15}/> Neto a entregar</div>
            <div className="font-display text-2xl font-semibold tabular-nums text-emerald-700 dark:text-emerald-200" data-testid="pos-pay-net">{money(c, net)}</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="pos-pay-cancel-button">Cancelar</Button>
          <Button onClick={() => pay.mutate()} disabled={pay.isPending || invalid} data-testid="pos-pay-confirm-button">
            <DollarSign size={16} className="mr-1.5"/> Confirmar pago {money(c, net)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PaidBreakdown({ ticket, c }) {
  return (
    <div className="mt-4 space-y-2">
      <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 flex items-center gap-2 text-emerald-700 dark:text-emerald-200" data-testid="pos-validator-paid-info">
        <CheckCircle2 size={16}/> Premio pagado {ticket.paid_at ? `el ${formatDate(ticket.paid_at)}` : ""}
        {ticket.payout_method ? ` · ${ticket.payout_method === "pago_movil" ? "Pago móvil" : "Efectivo"}` : ""}.
      </div>
      {ticket[`payout_deducted_${c.key}`] > 0 && (
        <div className="grid grid-cols-3 gap-2 text-center" data-testid="pos-paid-breakdown">
          <div className="rounded-lg border border-border p-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Ganado</div>
            <div className="font-mono tabular-nums text-sm">{money(c, ticket[`payout_total_${c.key}`])}</div>
          </div>
          <div className="rounded-lg border border-border p-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Descontado</div>
            <div className="font-mono tabular-nums text-sm text-rose-600 dark:text-rose-300">- {money(c, ticket[`payout_deducted_${c.key}`])}</div>
          </div>
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Neto entregado</div>
            <div className="font-mono tabular-nums text-sm text-emerald-700 dark:text-emerald-200">{money(c, ticket[`payout_net_${c.key}`])}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PosValidatorPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const gate = useOutletContext() || {};
  const locked = !!gate.suspended && !gate.consultActive;
  const [code, setCode] = useState("");
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [payOpen, setPayOpen] = useState(false);

  const { data: session } = useQuery({
    queryKey: ["cash-session"],
    queryFn: async () => (await api.get("/cash-sessions/current")).data,
    refetchInterval: 20000,
  });
  const needsSession = user?.role !== "admin" && !session;
  const c = curOf(ticket);

  const search = async (override) => {
    if (locked) return;
    const value = (typeof override === "string" ? override : code).replace(/\s+/g, "").toUpperCase();
    if (!value) return;
    if (typeof override === "string") setCode(value);
    setLoading(true); setTicket(null); setSuggestion(null);
    try {
      const { data } = await api.get(`/tickets/by-code/${encodeURIComponent(value)}`);
      setTicket(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "No encontrado";
      const m = msg.match(/¿Quiso decir ([A-Z0-9-]+)\?/);
      setSuggestion(m ? m[1] : null);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const onPaid = (t) => {
    setTicket(t); setPayOpen(false);
    qc.invalidateQueries({ queryKey: ["cash-session"] });
    qc.invalidateQueries({ queryKey: ["tickets"] });
    qc.invalidateQueries({ queryKey: ["cuadre"] });
  };

  return (
    <div className="relative min-h-[calc(100vh-72px)]" data-testid="pos-validator-page">
      {locked && <PosLockedOverlay />}
      <div className="p-3 md:p-4 max-w-3xl mx-auto">
        <div className="mb-4">
          <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Validar ticket</h1>
          <p className="text-sm text-muted-foreground">Ingresa el código impreso en el ticket para verificar y pagar premios.</p>
        </div>

        <Card className="bg-card border-border p-4">
          <div className="flex flex-col md:flex-row gap-2">
            <Input
              value={code}
              onChange={(e) => { setCode(e.target.value.toUpperCase()); setSuggestion(null); }}
              placeholder="Ej: AB2C-4XY9-Z7K"
              className="font-mono text-lg tracking-widest"
              onKeyDown={(e) => e.key === "Enter" && search()}
              disabled={locked}
              data-testid="pos-validator-code-input"
              autoFocus
            />
            <Button onClick={search} disabled={locked || loading || !code.trim()} data-testid="pos-validator-search-button">
              <Search size={16} className="mr-1.5"/> Buscar
            </Button>
          </div>
          {suggestion && (
            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm" data-testid="pos-validator-suggestion">
              <span>¿Quiso decir <span className="font-mono font-bold tracking-widest">{suggestion}</span>?</span>
              <Button size="sm" variant="outline" disabled={locked} onClick={() => search(suggestion)} data-testid="pos-validator-suggestion-button">Buscar este serial</Button>
            </div>
          )}
        </Card>

        {ticket && (
          <Card className="bg-card border-border p-4 mt-4" data-testid="pos-validator-status">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Código</div>
                <div className="font-mono text-2xl font-semibold text-primary" data-testid="pos-validator-ticket-code"><CopyCode code={ticket.code} size={16} /></div>
                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1" data-testid="pos-validator-issued-at">
                  <Clock size={12}/> Emitido {formatDate(ticket.created_at)}
                </div>
              </div>
              <div className="text-right">
                <StatusBadge status={ticket.status}/>
                <div className="mt-2 font-display text-2xl font-semibold tabular-nums" data-testid="pos-validator-total">{money(c, ticket[`total_${c.key}`])}</div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground" data-testid="pos-validator-currency">Moneda {ticket.currency}</div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-border p-3">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Premio total</div>
                <div className="font-display text-xl tabular-nums font-semibold text-emerald-700 dark:text-emerald-300" data-testid="pos-validator-payout-total">
                  {money(c, ticket[`payout_total_${c.key}`])}
                </div>
              </div>
              <div className="rounded-lg border border-border p-3">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Jugadas ganadoras</div>
                <div className="font-display text-xl font-semibold tabular-nums" data-testid="pos-validator-winner-count">
                  {ticket.plays.filter((p) => p.is_winner).length} / {ticket.plays.length}
                </div>
              </div>
            </div>

            <div className="mt-4 space-y-2" data-testid="pos-validator-plays">
              {ticket.plays.map((p, i) => <PlayRow key={i} p={p} i={i} ticket={ticket} c={c}/>)}
            </div>

            {ticket.status === "winner" && (
              <div className="mt-4 flex flex-col items-end gap-2">
                {needsSession && (
                  <div className="text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1.5" data-testid="pos-pay-need-session">
                    <Lock size={13}/> Abre caja para poder pagar premios.
                  </div>
                )}
                {gate.suspended && (
                  <div className="text-xs text-rose-700 dark:text-rose-300 flex items-center gap-1.5" data-testid="pos-pay-suspended">
                    <Lock size={13}/> Taquilla suspendida: pago de premios inhabilitado.
                  </div>
                )}
                <Button size="lg" onClick={() => setPayOpen(true)} disabled={needsSession || gate.suspended} data-testid="pos-validator-pay-button">
                  <DollarSign size={18} className="mr-2"/> Pagar premio {money(c, ticket[`payout_total_${c.key}`])}
                </Button>
              </div>
            )}
            {ticket.status === "paid" && <PaidBreakdown ticket={ticket} c={c}/>}
            {ticket.status === "lost" && (
              <div className="mt-4 rounded-lg border border-slate-500/30 bg-slate-500/10 p-3 text-slate-700 dark:text-slate-300 text-sm" data-testid="pos-validator-lost">
                Este ticket no resultó ganador en el sorteo.
              </div>
            )}
            {ticket.status === "active" && (
              <div className="mt-4 rounded-lg border border-sky-500/30 bg-sky-500/10 p-3 text-sky-700 dark:text-sky-200 text-sm" data-testid="pos-validator-pending">
                El sorteo aún no ha sido liquidado. Vuelve a validar después del resultado oficial.
              </div>
            )}
          </Card>
        )}

        {ticket && <PayDialog open={payOpen && !gate.suspended} onOpenChange={setPayOpen} ticket={ticket} c={c} onPaid={onPaid}/>}
      </div>
    </div>
  );
}
